import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { Plus, ShoppingCart, Users } from 'lucide-react'

import type {
    PackagingDraftItem as ApiPackagingDraftItem,
    SalesOrder,
} from '@/lib/api/sales-order-api'
import type { OrderItemDraft } from '@/components/forms/order-item-row'
import type { PackagingDraftItem } from '@/lib/packaging-draft'
import type { ProvinceWardValue } from '@/components/forms/province-ward-picker'
import type { LineSplitState } from '@/lib/material-split'
import type { PickedPackagingMaterial } from '@/components/packaging/packaging-material-picker'
import type { FeePayer } from '@/components/orders/cod-freight-fields'
import type { MoneyLine } from '@/lib/order-money'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CodFreightFields } from '@/components/orders/cod-freight-fields'
import {
    InvoiceBuyerFields,
    applyBuyerPatch,
    buildInvoiceBuyerPayload,
    invoiceBuyerToFormValue,
} from '@/components/forms/invoice-buyer-fields'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { useCustomer } from '@/hooks/use-customer-query'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { FormSection } from '@/components/forms/form-section'
import { OrderItemRow } from '@/components/forms/order-item-row'
import { OrderMaterialSubRows } from '@/components/forms/order-material-sub-rows'
import { PackagingMaterialPicker } from '@/components/packaging/packaging-material-picker'
import { OrderMaterialRow } from '@/components/forms/order-material-row'
import { OrderMaterialDetailRow } from '@/components/orders/order-material-detail-row'
import { buildManualRow, hydrateDraftItems } from '@/lib/packaging-draft'
import { newMaterialRowId, rowKeyOf } from '@/lib/material-row-id'
import { buildLineSortOrders, mergeOrderLines } from '@/lib/order-line-order'
import { newOrderItemDraft } from '@/components/forms/new-order-item-draft'
import { useOrderPackagingSync } from '@/hooks/use-order-packaging-sync'
import { ProvinceWardPicker } from '@/components/forms/province-ward-picker'
import { useProducts } from '@/hooks/use-product-query'
import {
    useOrderPackagingDraft,
    useUpdateOrder,
} from '@/hooks/use-sales-order-query'
import { useInvoiceSettings } from '@/hooks/use-invoice-query'
import { formatVND, formatVnd } from '@/lib/format'
import { effectiveMaterialUnitPrice } from '@/lib/material-split'
import { m } from '@/lib/paraglide/messages'
import { vatFromGross } from '@/lib/order-money'

interface Props {
    order: SalesOrder
    /** Sản phẩm/vật tư có được sửa không. false (đơn đã confirmed trở đi) → bảng
     *  hàng + vật tư hiển thị chỉ đọc và KHÔNG gửi lên (BE giữ nguyên), chỉ cho
     *  sửa nhóm thông tin chung. */
    itemsEditable: boolean
    hasActiveCarrierShipment?: boolean
    onSuccess: () => void
    onCancel: () => void
}

function buildInitialItems(order: SalesOrder): OrderItemDraft[] {
    return (order.items ?? []).map((item) => ({
        id: item.id,
        product_id: item.product_id,
        product_name: item.product_name,
        quantity: item.quantity,
        unit_price: item.unit_price,
        discount_percent: item.discount_percent,
        // Trạng thái tách/gộp đã lưu. Cờ SP + M + master enrich khi products load.
        material_price_split: item.material_price_split ?? false,
        // Thứ tự dòng đã lưu — để bảng hiện lại đúng thế xen kẽ sản phẩm/vật tư
        // người bán đã nhập (FD #358). Đơn cũ = 0 → fallback sản phẩm trước.
        row_seq: item.sort_order,
    }))
}

export function EditOrderForm({
    order,
    itemsEditable,
    hasActiveCarrierShipment = false,
    onSuccess,
    onCancel,
}: Props) {
    const updateOrder = useUpdateOrder()
    const workflow = useWorkflowTracking('order_edit', {
        attemptKey: order.id,
        operation: 'update',
    })
    const { data: productsRes } = useProducts({ per_page: 200 })
    const products = productsRes?.data ?? []
    const { data: invoiceSettings } = useInvoiceSettings()
    // VAT chỉnh được (2 chiều % ↔ tiền). Khởi tạo từ snapshot của ĐƠN nếu có
    // (đơn mới), ngược lại (đơn cũ) mặc định từ cài đặt global. vatTouched khởi
    // true khi đơn đã có VAT riêng → không để settings ghi đè.
    const [vatPercent, setVatPercent] = useState<number>(order.vat_percent ?? 0)
    const [vatAmountOverride, setVatAmountOverride] = useState<number | null>(
        null,
    )
    const vatTouched = useRef(order.vat_percent != null)
    // Người dùng tự đổi % hoặc tiền VAT trên form.
    const [vatEdited, setVatEdited] = useState(false)
    useEffect(() => {
        if (!vatTouched.current && invoiceSettings?.vat_percent != null) {
            setVatPercent(invoiceSettings.vat_percent)
        }
    }, [invoiceSettings?.vat_percent])

    const [customerName, setCustomerName] = useState(order.customer_name)
    const [customerPhone, setCustomerPhone] = useState(order.customer_phone)
    const [shippingAddress, setShippingAddress] = useState(
        order.shipping_address,
    )
    const [shipping, setShipping] = useState<ProvinceWardValue>({
        provinceCode: order.shipping_province_code ?? undefined,
        provinceName: order.shipping_province ?? undefined,
        wardCode: order.shipping_ward_code ?? undefined,
        wardName: order.shipping_ward ?? undefined,
    })
    const [productNotes, setProductNotes] = useState(order.product_notes ?? '')
    const [internalOrderCode, setInternalOrderCode] = useState(
        order.internal_order_code ?? '',
    )
    // Thu tiền (COD) + Thu cước vận chuyển — tri-state, undefined = "không chọn".
    const [collectCod, setCollectCod] = useState<boolean | undefined>(
        order.collect_cod ?? undefined,
    )
    const [feePayer, setFeePayer] = useState<FeePayer | undefined>(
        order.shipping_fee_payer,
    )
    // Người mua công ty (HĐĐT) — prefill từ đơn, ẩn sau flag dev-einvoice.
    const einvoiceEnabled = useFeatureFlag('dev-einvoice')
    const [invoiceBuyer, setInvoiceBuyer] = useState(
        invoiceBuyerToFormValue(order.invoice_buyer, order.issue_einvoice),
    )
    // Hồ sơ khách của đơn — dùng auto-fill thông tin công ty khi bật tick.
    const { data: buyerCustomer } = useCustomer(order.customer_id)
    const [items, setItems] = useState<OrderItemDraft[]>(
        buildInitialItems(order),
    )

    // Phase 4 — vật tư kèm đơn: load draft đã lưu lúc tạo đơn để hiển thị +
    // cho sale sửa. Autofill BOM sẽ tự merge từ items.
    const { data: draftData } = useOrderPackagingDraft(order.id)
    const [packagingItems, setPackagingItems] = useState<PackagingDraftItem[]>(
        [],
    )
    const [draftHydrated, setDraftHydrated] = useState(false)

    // Bộ đếm dòng dùng CHUNG cho sản phẩm và vật tư. Khởi tạo trên số lớn nhất
    // đơn đang có để dòng thêm mới luôn xuống cuối bảng, không chèn vào giữa
    // các dòng đã lưu (FD #358).
    const rowSeqRef = useRef(
        (order.items ?? []).reduce(
            (max, it) => Math.max(max, it.sort_order ?? 0),
            (order.items?.length ?? 0) - 1,
        ) + 1,
    )
    const nextRowSeq = () => rowSeqRef.current++
    useEffect(() => {
        if (draftHydrated || !draftData) return
        const hydrated = hydrateDraftItems(draftData)
        setPackagingItems(hydrated)
        // Vật tư đã lưu có thể mang số lớn hơn mọi dòng sản phẩm → đẩy bộ đếm
        // lên trên chúng để dòng thêm mới vẫn nằm cuối bảng.
        rowSeqRef.current = hydrated.reduce(
            (max, row) => Math.max(max, (row.row_seq ?? 0) + 1),
            rowSeqRef.current,
        )
        setDraftHydrated(true)
    }, [draftData, draftHydrated])

    // Enrich cờ price_includes_material + M + master từ products (một lần, khi
    // products load). Master = product.unit_price (catalog) để toggle reset đúng
    // ngay cả với dòng đã tách (unit_price đã = master − M lúc lưu).
    const [flagsHydrated, setFlagsHydrated] = useState(false)
    useEffect(() => {
        if (flagsHydrated || products.length === 0) return
        setItems((prev) =>
            prev.map((item) => {
                const product = products.find((p) => p.id === item.product_id)
                if (!product) return item
                return {
                    ...item,
                    price_includes_material:
                        product.price_includes_material ?? false,
                    material_price_per_unit:
                        product.material_price_per_unit ?? 0,
                    master_unit_price: product.unit_price,
                }
            }),
        )
        setFlagsHydrated(true)
    }, [products, flagsHydrated])

    const handleItemChange = (
        index: number,
        field: keyof OrderItemDraft,
        value: string | number | boolean,
    ) => {
        setItems((prev) =>
            prev.map((item, i) =>
                i === index ? { ...item, [field]: value } : item,
            ),
        )
    }

    const handleRemoveItem = (index: number) => {
        const removedId = items[index]?.id
        setItems((prev) => prev.filter((_, i) => i !== index))
        // Bỏ vật tư inline gắn dòng vừa xoá để không còn "mồ côi" tính tiền.
        if (removedId) {
            setPackagingItems((prev) =>
                prev.filter((p) => p.line_key !== removedId),
            )
        }
    }

    // Vật tư của đơn giờ CHỈ do nhân viên tự thêm (#M-16) — đơn không tự sinh
    // theo định mức nữa, việc đó chuyển sang phiếu nhập kho hàng bán. Hook chỉ
    // còn bù số tồn cho các dòng thêm tay.
    const { availableByMaterial } = useOrderPackagingSync({
        value: packagingItems,
        onChange: setPackagingItems,
    })

    // Vật tư THÊM TAY render ngang hàng sản phẩm — khớp theo KHOÁ DÒNG vì cùng
    // một vật tư có thể nằm trên nhiều dòng.
    const patchMaterialRow = (
        rowKey: string,
        patch: (p: PackagingDraftItem) => PackagingDraftItem,
    ) =>
        setPackagingItems((prev) =>
            prev.map((p) => (rowKeyOf(p) === rowKey ? patch(p) : p)),
        )

    const handleMaterialRowQty = (rowKey: string, v: string) =>
        patchMaterialRow(rowKey, (p) => ({
            ...p,
            quantity: Math.max(0, Number(v) || 0),
        }))

    const handleMaterialRowPrice = (rowKey: string, v: string) =>
        patchMaterialRow(rowKey, (p) => ({
            ...p,
            unit_price: Math.max(0, Number(v) || 0),
        }))

    const handleMaterialRowDiscount = (rowKey: string, v: string) =>
        patchMaterialRow(rowKey, (p) => ({
            ...p,
            discount_percent: Math.min(100, Math.max(0, Number(v) || 0)),
        }))

    const handleMaterialRowRemove = (rowKey: string) =>
        setPackagingItems((prev) => prev.filter((p) => rowKeyOf(p) !== rowKey))

    const handleAddMaterialRow = (mat: PickedPackagingMaterial) =>
        setPackagingItems((prev) => [
            ...prev,
            {
                ...buildManualRow(
                    prev,
                    mat,
                    availableByMaterial,
                    newMaterialRowId(),
                ),
                row_seq: nextRowSeq(),
            },
        ])

    // Vật tư thêm tay = dòng không gắn dòng đơn nào.
    const manualMaterialRows = packagingItems.filter((p) => !p.line_key)

    // Thứ tự hiển thị của bảng hàng hóa: sản phẩm và vật tư thêm tay trộn chung
    // theo row_seq. Vật tư theo định mức không nằm ở đây — chúng luôn render
    // ngay dưới dòng sản phẩm của mình.
    const displayRows = mergeOrderLines(
        items.map((it) => it.row_seq),
        manualMaterialRows.map((mat) => mat.row_seq),
    )

    // Vật tư CÓ GIÁ inline dưới dòng SP: khớp theo (line_key, material_id).
    // Giống sales-order-form để 3 màn (tạo/sửa/chi tiết) hiển thị đồng nhất.
    const handleMaterialQtyChange = (
        materialId: string,
        lineKey: string,
        qtyStr: string,
    ) => {
        const qty = Math.max(0, Number(qtyStr) || 0)
        setPackagingItems((prev) =>
            prev.map((p) =>
                p.packaging_material_id === materialId && p.line_key === lineKey
                    ? { ...p, quantity: qty }
                    : p,
            ),
        )
    }

    const handleMaterialPriceChange = (
        materialId: string,
        lineKey: string,
        priceStr: string,
    ) => {
        const price = Math.max(0, Number(priceStr) || 0)
        setPackagingItems((prev) =>
            prev.map((p) =>
                p.packaging_material_id === materialId && p.line_key === lineKey
                    ? { ...p, unit_price: price }
                    : p,
            ),
        )
    }

    const handleMaterialDiscountChange = (
        materialId: string,
        lineKey: string,
        pctStr: string,
    ) => {
        const pct = Math.min(100, Math.max(0, Number(pctStr) || 0))
        setPackagingItems((prev) =>
            prev.map((p) =>
                p.packaging_material_id === materialId && p.line_key === lineKey
                    ? { ...p, discount_percent: pct }
                    : p,
            ),
        )
    }

    const handleMaterialRemove = (materialId: string, lineKey: string) => {
        setPackagingItems((prev) =>
            prev.filter(
                (p) =>
                    !(
                        p.packaging_material_id === materialId &&
                        p.line_key === lineKey
                    ),
            ),
        )
    }

    const addItem = () => {
        setItems((prev) => [
            ...prev,
            { ...newOrderItemDraft(), row_seq: nextRowSeq() },
        ])
    }

    const itemSubtotal = items.reduce(
        (sum, item) =>
            sum +
            item.quantity * item.unit_price * (1 - item.discount_percent / 100),
        0,
    )
    // line_key (order_item.id) → trạng thái tách/gộp. Dòng GỘP → giá vật tư hiệu
    // lực = 0 (giá nằm trong giá SP).
    const lineStateByKey = new Map<string, LineSplitState>(
        items.map((it) => [
            it.id ?? '',
            {
                priceIncludesMaterial: it.price_includes_material ?? false,
                materialPriceSplit: it.material_price_split ?? false,
            },
        ]),
    )
    const effectivePriceOf = (p: PackagingDraftItem) =>
        effectiveMaterialUnitPrice(
            p.unit_price ?? 0,
            lineStateByKey.get(p.line_key) ?? {
                priceIncludesMaterial: false,
                materialPriceSplit: false,
            },
        )

    // Tiền vật tư (giá vật tư) — chịu chiết khấu dòng như dòng hàng, cộng vào
    // base cùng SP trước VAT. Dòng GỘP dùng giá hiệu lực 0 (đã gồm trong giá SP).
    const materialTotal = packagingItems.reduce(
        (sum, p) =>
            sum +
            effectivePriceOf(p) *
                (p.quantity ?? 0) *
                (1 - (p.discount_percent ?? 0) / 100),
        0,
    )
    const baseAmount = itemSubtotal + materialTotal
    const roundedBase = Math.round(baseAmount)
    // M-78: VAT mặc định = tổng theo giá gồm VAT từng đơn vị − tiền hàng, để
    // tổng tròn theo giá niêm yết (giá chưa VAT thường lẻ: 100.000 / 1,08).
    const moneyLines: MoneyLine[] = [
        ...items.map((item) => ({
            quantity: item.quantity,
            unitPrice: item.unit_price,
            discountPercent: item.discount_percent,
        })),
        ...packagingItems.map((p) => ({
            quantity: p.quantity ?? 0,
            unitPrice: effectivePriceOf(p),
            discountPercent: p.discount_percent ?? 0,
        })),
    ]
    // Hàng/vật tư chưa đổi và người dùng chưa chỉnh VAT → giữ nguyên tiền VAT
    // của đơn (đơn cũ chưa snapshot thì suy từ tổng đã lưu), để sửa ghi chú, địa
    // chỉ… không làm tổng và công nợ đơn cũ lệch 1–2đ.
    // Tiền hàng của đơn như đã lưu (dòng hàng gốc + vật tư có giá đã lưu), tính
    // bằng cùng công thức với form để so "hàng chưa đổi" — vật tư tải chậm hay
    // cờ gộp giá cập nhật sau đều không làm lệch phép so.
    const savedMaterials = useMemo(
        () => hydrateDraftItems(draftData ?? []),
        [draftData],
    )
    const savedBase = Math.round(
        (order.items ?? []).reduce(
            (sum, it) =>
                sum +
                it.quantity *
                    it.unit_price *
                    (1 - (it.discount_percent ?? 0) / 100),
            0,
        ) +
            savedMaterials.reduce(
                (sum, p) =>
                    sum +
                    effectivePriceOf(p) *
                        (p.quantity ?? 0) *
                        (1 - (p.discount_percent ?? 0) / 100),
                0,
            ),
    )
    const storedVat =
        order.vat_amount != null
            ? Math.round(order.vat_amount)
            : (order.discount_percent ?? 0) === 0
              ? Math.round(order.total_amount) - savedBase
              : null
    const keepStoredVat =
        !vatEdited &&
        roundedBase === savedBase &&
        storedVat != null &&
        storedVat >= 0
    // VAT làm tròn về số nguyên (VND không có subunit). Override = tiền gõ tay.
    const vatAmount =
        vatAmountOverride != null
            ? vatAmountOverride
            : keepStoredVat && storedVat != null
              ? storedVat
              : vatPercent > 0
                ? vatFromGross(moneyLines, 0, vatPercent, roundedBase)
                : 0
    const totalAmount = roundedBase + vatAmount

    useEffect(() => {
        setVatAmountOverride(null)
    }, [roundedBase])

    const handleVatPercentChange = (v: number) => {
        vatTouched.current = true
        setVatEdited(true)
        setVatAmountOverride(null)
        setVatPercent(Number.isFinite(v) && v >= 0 ? v : 0)
    }
    const handleVatAmountChange = (amt: number) => {
        vatTouched.current = true
        setVatEdited(true)
        const safe = Number.isFinite(amt) && amt >= 0 ? Math.round(amt) : 0
        setVatAmountOverride(safe)
        setVatPercent(
            roundedBase > 0
                ? Math.round((safe / roundedBase) * 100 * 100) / 100
                : 0,
        )
    }

    const canSubmit =
        customerName.trim().length >= 2 &&
        customerPhone.trim().length >= 8 &&
        shippingAddress.trim().length >= 5 &&
        !updateOrder.isPending

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!canSubmit) {
            workflow.step('validation_failed')
            return
        }

        // Dòng đơn thực gửi lên (BE tạo lại theo đúng thứ tự này). Map
        // order_item.id → index để gắn lại order_item_id cho dòng vật tư.
        const submittedItems = items.filter((item) => item.product_id)
        const lineIndexByKey = new Map(
            submittedItems.map((item, i) => [item.id ?? '', i]),
        )
        // Thứ tự người bán đang thấy (sản phẩm xen kẽ vật tư) đánh lại thành
        // sort_order 0..n-1 để lưu và mở lại đúng thế đó.
        const payloadMaterials = packagingItems.filter((pi) => pi.quantity > 0)
        const { itemSortOrders, materialSortOrders } = buildLineSortOrders({
            items: submittedItems.map((item) => ({
                key: item.id ?? '',
                row_seq: item.row_seq,
            })),
            materials: payloadMaterials.map((pi) => ({
                line_key: pi.line_key,
                row_seq: pi.row_seq,
            })),
        })

        // Nhóm thông tin chung — luôn gửi. Sản phẩm/vật tư chỉ gửi khi được phép
        // sửa (đơn new/awaiting); ngược lại bỏ hẳn để BE giữ nguyên hàng + tổng
        // tiền + packaging draft (nếu gửi, BE sẽ chặn với lỗi 409).
        const itemsPayload = itemsEditable
            ? {
                  vat_percent: vatPercent,
                  vat_amount: vatAmount,
                  items: submittedItems.map((item, idx) => ({
                      product_id: item.product_id,
                      quantity: item.quantity,
                      unit_price: item.unit_price,
                      discount_percent: item.discount_percent,
                      material_price_split:
                          item.material_price_split || undefined,
                      sort_order: itemSortOrders[idx],
                  })),
                  packaging_items: payloadMaterials.map((pi, idx) => ({
                      packaging_material_id: pi.packaging_material_id,
                      quantity: pi.quantity,
                      source: pi.source,
                      // Dòng GỘP gửi giá 0 (giá vật tư trong giá SP); TÁCH gửi
                      // giá cấu hình. Vẫn trừ kho/PXK (chỉ giá 0).
                      unit_price: effectivePriceOf(pi),
                      discount_percent: pi.discount_percent,
                      // Rỗng cho vật tư thêm tay → OMIT (BE bind uuid.UUID,
                      // chuỗi '' gây "Invalid request body").
                      product_id: pi.product_id || undefined,
                      // Mọi vật tư gắn dòng đơn (line_key = order_item.id) →
                      // index dòng để BE map lại order_item_id.
                      order_item_index: pi.line_key
                          ? (lineIndexByKey.get(pi.line_key) ?? undefined)
                          : undefined,
                      sort_order: materialSortOrders[idx],
                  })),
              }
            : {}

        try {
            await workflow.run(
                () =>
                    updateOrder.mutateAsync({
                        id: order.id,
                        data: {
                            customer_name: customerName.trim(),
                            customer_phone: customerPhone.trim(),
                            shipping_address: shippingAddress.trim(),
                            shipping_province:
                                shipping.provinceName || undefined,
                            shipping_province_code:
                                shipping.provinceCode || undefined,
                            shipping_ward: shipping.wardName || undefined,
                            shipping_ward_code: shipping.wardCode || undefined,
                            product_notes: productNotes.trim() || null,
                            internal_order_code:
                                internalOrderCode.trim() || null,
                            // null = bỏ chọn (gửi tường minh để BE xoá về NULL).
                            collect_cod: collectCod ?? null,
                            // '' = bỏ chọn bên trả cước (gửi tường minh để BE xoá về
                            // NULL, thay vì undefined bị bỏ qua). Không gửi shipping_fee:
                            // đổi bên trả cước không phải lý do để xoá số cước đang có
                            // (cước hãng ghi vào cùng cột khi lên vận đơn).
                            shipping_fee_payer: feePayer ?? '',
                            // Cổng "Xuất hóa đơn điện tử": patch theo tick (undefined khi flag tắt).
                            issue_einvoice: einvoiceEnabled
                                ? invoiceBuyer.enabled
                                : undefined,
                            // HĐĐT người mua công ty: undefined=giữ nguyên, {}=xóa, data=upsert.
                            invoice_buyer: einvoiceEnabled
                                ? buildInvoiceBuyerPayload(invoiceBuyer, 'edit')
                                : undefined,
                            ...itemsPayload,
                        },
                    }),
                (result) => result.data?.id,
            )
            onSuccess()
        } catch {
            // error handled by mutation hook toast
        }
    }

    return (
        <form
            onSubmit={handleSubmit}
            onChangeCapture={() => workflow.interact('details')}
        >
            {/* 2 cột giống màn Tạo: trái = thông tin đơn, phải = sản phẩm +
                vật tư + tổng tiền/hành động (footer dính đáy page). */}
            <div className="grid gap-5 lg:grid-cols-[minmax(360px,420px)_minmax(0,1fr)] lg:items-start">
                {/* LEFT — customer + delivery + notes */}
                <div className="space-y-5">
                    <FormSection
                        icon={<Users />}
                        title={m.order_customer_info()}
                    >
                        <div className="space-y-1.5">
                            <Label htmlFor="edit-name">
                                {m.customer_name_label()} *
                            </Label>
                            <Input
                                id="edit-name"
                                value={customerName}
                                onChange={(e) =>
                                    setCustomerName(e.target.value)
                                }
                                required
                                minLength={2}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="edit-phone">
                                {m.customer_phone_label()} *
                            </Label>
                            <Input
                                id="edit-phone"
                                type="tel"
                                value={customerPhone}
                                onChange={(e) =>
                                    setCustomerPhone(e.target.value)
                                }
                                required
                                minLength={8}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="edit-address">
                                {m.customer_shipping_address()} *
                            </Label>
                            <Input
                                id="edit-address"
                                value={shippingAddress}
                                onChange={(e) =>
                                    setShippingAddress(e.target.value)
                                }
                                required
                                minLength={5}
                            />
                        </div>
                        <ProvinceWardPicker
                            value={shipping}
                            onChange={setShipping}
                        />

                        {/* Ghi chú + mã nội bộ gộp vào cuối, giống màn Tạo. */}
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                            <div className="space-y-1.5">
                                <Label htmlFor="edit-internal-code">
                                    {m.order_internal_code_label()}
                                </Label>
                                <Input
                                    id="edit-internal-code"
                                    value={internalOrderCode}
                                    onChange={(e) =>
                                        setInternalOrderCode(e.target.value)
                                    }
                                    maxLength={64}
                                    placeholder={m.order_internal_code_placeholder()}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="edit-notes">
                                    {m.customer_product_notes()}
                                </Label>
                                <textarea
                                    id="edit-notes"
                                    value={productNotes}
                                    onChange={(e) =>
                                        setProductNotes(e.target.value)
                                    }
                                    rows={2}
                                    placeholder={m.product_notes_placeholder()}
                                    className="border-input focus-visible:border-ring w-full resize-y rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs outline-none"
                                />
                            </div>
                        </div>

                        {/* Thu tiền (COD) + Thu cước vận chuyển — dùng chung
                            component với màn tạo đơn. */}
                        <CodFreightFields
                            idPrefix="order-edit"
                            collectCod={collectCod}
                            onCollectCodChange={setCollectCod}
                            feePayer={feePayer}
                            onFeePayerChange={setFeePayer}
                            customerFreightDisabled={hasActiveCarrierShipment}
                            customerFreightDisabledReason={
                                hasActiveCarrierShipment
                                    ? m.order_edit_freight_locked_by_shipment()
                                    : undefined
                            }
                        />
                    </FormSection>
                </div>

                {/* RIGHT — sản phẩm + vật tư + tổng tiền */}
                <div className="space-y-5">
                    {/* Sản phẩm + vật tư: chỉ sửa được khi đơn
                        new/awaiting_packaging. Từ confirmed trở đi chỉ đọc. */}
                    {itemsEditable ? (
                        <>
                            <FormSection
                                icon={<ShoppingCart />}
                                title={m.order_products()}
                                action={
                                    <div className="flex items-center gap-2">
                                        <div className="w-56">
                                            <PackagingMaterialPicker
                                                onPick={handleAddMaterialRow}
                                                placeholder={m.packaging_add_material()}
                                            />
                                        </div>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            onClick={addItem}
                                        >
                                            <Plus className="size-4" />
                                            {m.common_add()}
                                        </Button>
                                    </div>
                                }
                            >
                                <div className="overflow-x-auto rounded-lg border">
                                    <table className="w-full table-fixed text-sm">
                                        <colgroup>
                                            <col className="w-[30%]" />
                                            <col className="w-[9%]" />
                                            <col className="w-[9%]" />
                                            <col className="w-[18%]" />
                                            <col className="w-[9%]" />
                                            <col className="w-[17%]" />
                                            <col className="w-[8%]" />
                                        </colgroup>
                                        <thead className="bg-primary/5 text-[11px] uppercase tracking-wider text-primary/80">
                                            <tr>
                                                <th className="px-3 py-2.5 text-left font-semibold">
                                                    {m.order_products()}
                                                </th>
                                                <th className="px-3 py-2.5 text-left font-semibold">
                                                    {m.order_unit_col()}
                                                </th>
                                                <th className="px-3 py-2.5 text-left font-semibold">
                                                    {m.order_quantity()}
                                                </th>
                                                <th className="px-3 py-2.5 text-left font-semibold">
                                                    {m.order_unit_price()}
                                                </th>
                                                <th className="px-3 py-2.5 text-left font-semibold">
                                                    {m.order_discount_short()}
                                                </th>
                                                <th className="px-3 py-2.5 text-right font-semibold">
                                                    {m.order_subtotal()}
                                                </th>
                                                <th className="px-3 py-2.5" />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {displayRows.map((row) => {
                                                if (row.kind === 'material') {
                                                    const mat =
                                                        manualMaterialRows[
                                                            row.index
                                                        ]
                                                    return (
                                                        <OrderMaterialRow
                                                            key={rowKeyOf(mat)}
                                                            item={mat}
                                                            onQtyChange={
                                                                handleMaterialRowQty
                                                            }
                                                            onPriceChange={
                                                                handleMaterialRowPrice
                                                            }
                                                            onDiscountChange={
                                                                handleMaterialRowDiscount
                                                            }
                                                            onRemove={
                                                                handleMaterialRowRemove
                                                            }
                                                        />
                                                    )
                                                }
                                                const index = row.index
                                                const item = items[index]
                                                return (
                                                    <Fragment
                                                        key={item.id ?? index}
                                                    >
                                                        <OrderItemRow
                                                            item={item}
                                                            index={index}
                                                            products={products}
                                                            onChange={
                                                                handleItemChange
                                                            }
                                                            onRemove={
                                                                handleRemoveItem
                                                            }
                                                        />
                                                        <OrderMaterialSubRows
                                                            materials={
                                                                item.id
                                                                    ? packagingItems.filter(
                                                                          (p) =>
                                                                              p.line_key ===
                                                                              item.id,
                                                                      )
                                                                    : []
                                                            }
                                                            effectivePriceOf={
                                                                effectivePriceOf
                                                            }
                                                            onQtyChange={
                                                                handleMaterialQtyChange
                                                            }
                                                            onPriceChange={
                                                                handleMaterialPriceChange
                                                            }
                                                            onDiscountChange={
                                                                handleMaterialDiscountChange
                                                            }
                                                            onRemove={
                                                                handleMaterialRemove
                                                            }
                                                        />
                                                    </Fragment>
                                                )
                                            })}
                                        </tbody>
                                        <tfoot className="border-t bg-muted/30 tabular-nums">
                                            <tr>
                                                <td
                                                    colSpan={5}
                                                    className="px-3 py-2 text-right text-muted-foreground"
                                                >
                                                    {m.order_items_subtotal()}
                                                </td>
                                                <td className="px-3 py-2 text-right">
                                                    {formatVND(itemSubtotal)}
                                                </td>
                                                <td />
                                            </tr>
                                            {materialTotal > 0 && (
                                                <tr>
                                                    <td
                                                        colSpan={5}
                                                        className="px-3 py-2 text-right text-muted-foreground"
                                                    >
                                                        {m.order_material_total()}
                                                    </td>
                                                    <td className="px-3 py-2 text-right">
                                                        {formatVND(
                                                            materialTotal,
                                                        )}
                                                    </td>
                                                    <td />
                                                </tr>
                                            )}
                                            <tr>
                                                <td
                                                    colSpan={5}
                                                    className="px-3 py-2 text-right text-muted-foreground"
                                                >
                                                    <span className="inline-flex items-center justify-end gap-1.5">
                                                        {m.order_vat_field_label()}
                                                        <input
                                                            type="number"
                                                            min={0}
                                                            max={100}
                                                            step="0.1"
                                                            inputMode="decimal"
                                                            aria-label={m.order_vat_field_label()}
                                                            value={vatPercent}
                                                            onChange={(e) =>
                                                                handleVatPercentChange(
                                                                    e.target
                                                                        .valueAsNumber,
                                                                )
                                                            }
                                                            className="focus-visible:border-ring w-16 rounded-md border bg-transparent px-2 py-1 text-right text-sm outline-none"
                                                        />
                                                        %:
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 text-right">
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        step="1"
                                                        inputMode="numeric"
                                                        aria-label={m.order_vat_amount_label()}
                                                        value={Math.round(
                                                            vatAmount,
                                                        )}
                                                        onChange={(e) =>
                                                            handleVatAmountChange(
                                                                e.target
                                                                    .valueAsNumber,
                                                            )
                                                        }
                                                        className="focus-visible:border-ring w-28 rounded-md border bg-transparent px-2 py-1 text-right text-sm outline-none"
                                                    />
                                                </td>
                                                <td />
                                            </tr>
                                            {/* Tổng cộng — desktop hiển thị ngay
                                                dưới VAT; mobile/tablet dùng thanh
                                                dính đáy bên dưới. */}
                                            <tr className="hidden border-t lg:table-row">
                                                <td
                                                    colSpan={5}
                                                    className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                                                >
                                                    {m.order_grand_total()}
                                                </td>
                                                <td className="px-3 py-2.5 text-right text-base font-bold text-primary">
                                                    {formatVND(totalAmount)}
                                                </td>
                                                <td />
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </FormSection>
                        </>
                    ) : (
                        <ReadOnlyItemsSummary order={order} />
                    )}

                    {/* Người mua công ty (HĐĐT) — dưới phần Sản phẩm để cân 2 cột;
                        chỉ hiện khi bật e-invoice. */}
                    {einvoiceEnabled && (
                        <InvoiceBuyerFields
                            idPrefix="order-edit"
                            value={invoiceBuyer}
                            onChange={(patch) =>
                                setInvoiceBuyer((prev) =>
                                    applyBuyerPatch(prev, patch, buyerCustomer),
                                )
                            }
                        />
                    )}

                    {/* Thanh hành động. Mobile/tablet: dính đáy, hiện tổng + nút.
                        Desktop (lg): bỏ chrome card + không dính, ẩn tổng (đã nằm
                        ở tfoot bảng SP / bảng chỉ đọc), chỉ còn hàng nút canh phải
                        ngay dưới phần tổng cộng. */}
                    <div className="sticky bottom-0 z-10 flex flex-col gap-3 rounded-xl border bg-card/95 px-5 py-3.5 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-card/80 sm:flex-row sm:items-center sm:justify-between lg:static lg:z-auto lg:justify-end lg:rounded-none lg:border-0 lg:bg-transparent lg:px-0 lg:py-0 lg:shadow-none lg:backdrop-blur-none lg:supports-[backdrop-filter]:bg-transparent">
                        <div className="lg:hidden">
                            <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                                {m.order_grand_total()}
                            </div>
                            <div className="text-2xl font-bold tabular-nums text-primary">
                                {formatVND(
                                    itemsEditable
                                        ? totalAmount
                                        : Math.round(order.total_amount),
                                )}
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    workflow.finish('closed')
                                    onCancel()
                                }}
                            >
                                {m.common_cancel()}
                            </Button>
                            <Button
                                type="submit"
                                size="lg"
                                disabled={!canSubmit}
                            >
                                {updateOrder.isPending
                                    ? m.common_saving()
                                    : m.order_save_changes()}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    )
}

// ReadOnlyItemsSummary — bảng sản phẩm chỉ đọc, dùng khi đơn đã qua new/awaiting
// nên không cho sửa hàng/vật tư nữa. Chỉ hiển thị để người sửa thông tin chung
// vẫn thấy nội dung đơn; tổng tiền lấy từ đơn (không tính lại).
//
// Vật tư phải hiển thị y như màn chi tiết: nếu chỉ render order.items thì vật tư
// (đã gồm trong total_amount) biến mất, gây lệch "thành tiền SP" vs "tổng cộng".
function ReadOnlyItemsSummary({ order }: { order: SalesOrder }) {
    const items = order.items ?? []
    const { data: packagingDraft = [] } = useOrderPackagingDraft(order.id)
    const { data: invoiceSettings } = useInvoiceSettings()
    // Ưu tiên VAT snapshot của đơn; đơn cũ fallback global.
    const vatPercent = order.vat_percent ?? invoiceSettings?.vat_percent ?? 0

    // Vật tư gắn dòng SP (order_item_id set) → sub-row dưới đúng sản phẩm. Vật tư
    // order-level (order_item_id null) → dòng ngang hàng sản phẩm. Nguồn = draft
    // đã lưu (KHÔNG recompute BOM) như màn chi tiết.
    const materialsByItem = new Map<string, ApiPackagingDraftItem[]>()
    for (const d of packagingDraft) {
        if (d.quantity > 0 && d.order_item_id) {
            const list = materialsByItem.get(d.order_item_id) ?? []
            list.push(d)
            materialsByItem.set(d.order_item_id, list)
        }
    }
    const manualMaterials = packagingDraft.filter(
        (d) => !d.order_item_id && d.quantity > 0,
    )
    // Bảng chỉ đọc cũng bám sort_order đã lưu để thứ tự xen kẽ sản phẩm/vật tư
    // giống hệt màn chi tiết đơn (FD #358).
    const readonlyRows = mergeOrderLines(
        items.map((it) => it.sort_order),
        manualMaterials.map((d) => d.sort_order),
    )

    // Tạm tính + VAT suy ngược từ tổng đơn (tổng đã chốt phía server) để luôn
    // khớp Tổng cộng, không phụ thuộc việc cộng lại từng dòng.
    const total = Math.round(order.total_amount)
    // Tiền VAT: ưu tiên snapshot của đơn; đơn cũ suy ngược từ tổng như trước.
    const vatAmount =
        order.vat_amount != null
            ? Math.round(order.vat_amount)
            : vatPercent > 0
              ? Math.round(total - Math.round(total / (1 + vatPercent / 100)))
              : 0
    const subtotalPreVat = total - vatAmount

    return (
        <div className="space-y-2">
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
                {m.order_edit_items_locked_notice()}
            </p>
            <Label>{m.order_products()}</Label>
            {/* Dùng chung primitive Table với màn chi tiết + OrderMaterialDetailRow
                để dòng sản phẩm và dòng vật tư cùng padding (p-3) → các cột SL /
                đơn giá / CK / thành tiền thẳng hàng. */}
            <div className="overflow-x-auto rounded-lg border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.order_products()}</TableHead>
                            <TableHead className="text-right">SL</TableHead>
                            <TableHead className="text-right">
                                {m.order_unit_price()}
                            </TableHead>
                            <TableHead className="text-right">CK %</TableHead>
                            <TableHead className="text-right">
                                {m.order_subtotal()}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {readonlyRows.map((row) => {
                            if (row.kind === 'material') {
                                const d = manualMaterials[row.index]
                                return (
                                    <OrderMaterialDetailRow
                                        key={d.id}
                                        d={d}
                                        variant="standalone"
                                    />
                                )
                            }
                            const item = items[row.index]
                            return (
                                <Fragment key={item.id}>
                                    <TableRow>
                                        <TableCell>
                                            {item.product_name}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {item.quantity}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {formatVnd(item.unit_price)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {item.discount_percent}%
                                        </TableCell>
                                        <TableCell className="text-right font-medium">
                                            {formatVnd(item.subtotal)}
                                        </TableCell>
                                    </TableRow>
                                    {(materialsByItem.get(item.id) ?? []).map(
                                        (d) => (
                                            <OrderMaterialDetailRow
                                                key={d.id}
                                                d={d}
                                                variant="sub"
                                            />
                                        ),
                                    )}
                                </Fragment>
                            )
                        })}
                        {vatPercent > 0 && (
                            <>
                                <TableRow className="bg-muted/30">
                                    <TableCell
                                        colSpan={4}
                                        className="text-right text-muted-foreground"
                                    >
                                        {m.order_items_subtotal()}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        {formatVnd(subtotalPreVat)}
                                    </TableCell>
                                </TableRow>
                                <TableRow className="bg-muted/30">
                                    <TableCell
                                        colSpan={4}
                                        className="text-right text-muted-foreground"
                                    >
                                        {m.order_vat_label({
                                            percent: String(vatPercent),
                                        })}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        {formatVnd(vatAmount)}
                                    </TableCell>
                                </TableRow>
                            </>
                        )}
                        <TableRow className="bg-muted/30 font-bold">
                            <TableCell colSpan={4} className="text-right">
                                {m.order_grand_total()}
                            </TableCell>
                            <TableCell className="text-right">
                                {formatVnd(order.total_amount)}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </div>
        </div>
    )
}
